import React, { FC, useState, useEffect, ReactElement, useRef } from "react";
import { MatterFilter, AscDesc, MatterStatus, MatterStatusSelectorDropdown } from "@design-system";
import { Modal, Typography, Button, Card, List, Tag, Alert, Empty, Row, Col, Tooltip } from "antd";
import { useQuery, useLazyQuery } from "@apollo/client";
import { useMutation, useSettings } from "../hooks";
import { USER_DATA_QUERY } from "~/hooks/user-data/user-data";
import { useFilteredMattersLazyQuery } from "~/hooks/matters";
import {
  FIND_ACCRUED_FEES_FOR_USER,
  FIND_SIMILAR_EXTERNAL_MATTERS_FOR_MATTERS,
  NOTIFICATIONS_QUERY,
} from "~/graphql/query";
import { UPDATE_MATTER, UPDATE_QUICK_UPDATE_SHOWN_AT, QUICK_LINK_MATTER } from "~/graphql/mutation";
import { Userpilot } from "userpilot";
import {
  LinkOutlined,
  ArrowRightOutlined,
  DownOutlined,
  UpOutlined,
  SearchOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import styles from "./quick-link-modal-container.css";

const {
  "quick-link-container": quickLinkContainerClassName,
  "quick-link-card": quickLinkCardClassName,
  "high-relevance-matter": highRelevanceMatterClassName,
  "stale-matter": staleMatterClassName,
  "stale-matter-alert": staleMatterAlertClassName,
  "matter-title": matterTitleClassName,
  "matter-metadata": matterMetadataClassName,
  "comparison-row": comparisonRowClassName,
  "vpd-matter-col": vpdMatterColClassName,
  "arrow-col": arrowColClassName,
  "arrow-container": arrowContainerClassName,
  "arrow-icon": arrowIconClassName,
  "external-matter-col": externalMatterColClassName,
  "inner-card": innerCardClassName,
  "vpd-matter-card": vpdMatterCardClassName,
  "external-matter-card": externalMatterCardClassName,
  "high-relevance-card": highRelevanceCardClassName,
  "card-content": cardContentClassName,
  "confidence-tag": confidenceTagClassName,
  "score-tag": scoreTagClassName,
  "external-title": externalTitleClassName,
  "external-description": externalDescriptionClassName,
  "base-card": baseCardClassName,
  "expanded-matches-container": expandedMatchesContainerClassName,
  "expanded-matches-header": expandedMatchesHeaderClassName,
  "expanded-matches-title": expandedMatchesTitleClassName,
  "expanded-matches-subtitle": expandedMatchesSubtitleClassName,
  "expanded-matches-scroll": expandedMatchesScrollClassName,
  "match-item": matchItemClassName,
  "high-relevance-item": highRelevanceItemClassName,
  "empty-state": emptyStateClassName,
  "action-buttons-container": actionButtonsContainerClassName,
  "link-matter-button": linkMatterButtonClassName,
  "show-more-button": showMoreButtonClassName,
  "no-actions": noActionsClassName,
  "confirm-link-row": confirmLinkRowClassName,
  "confirm-link-details": confirmLinkDetailsClassName,
  "confirm-row": confirmRowClassName,
  "confirm-section": confirmSectionClassName,
  "confirm-section-title": confirmSectionTitleClassName,
  "confirm-section-metadata": confirmSectionMetadataClassName,
  "confirm-metadata-item": confirmMetadataItemClassName,
  "confirm-practice-area": confirmPracticeAreaClassName,
  "confirm-arrow": confirmArrowClassName,
  "confirm-client-info": confirmClientInfoClassName,
  "confirm-action-buttons": confirmActionButtonsClassName,
  "link-and-set-live-button": linkAndSetLiveButtonClassName,
  "search-bar-container": searchBarContainerClassName,
  "search-input-wrapper": searchInputWrapperClassName,
  "search-input": searchInputClassName,
  "search-clear-button": searchClearButtonClassName,
  "search-icon": searchIconClassName,
  "text-match-highlight": textMatchHighlightClassName,
  "search-button": searchButtonClassName,
  "search-button-active": searchButtonActiveClassName,
  "full-modal-expanded-view": fullModalExpandedViewClassName,
  "match-icon": matchIconClassName,
  "no-match-icon": noMatchIconClassName,
  "high-confidence-match-card": highConfidenceMatchCardClassName,
  "stale-matter-card-buttons": staleMatterCardButtonsClassName,
} = styles as any;

const RELEVANCE_THRESHOLD = 0.25; // Minimum threshold for relevance
const RELEVANCE_GAP_THRESHOLD = 0.4; // Required gap between top and second scores

/**
 * Determines if a match is a high confidence match based on the relevance scores.
 * A match is considered high confidence if:
 * 1. The top score meets the minimum threshold
 * 2. Either there's only one match, or there's a significant gap between the first and second best scores
 */
const isHighConfidenceMatch = (scores: (number | null | undefined)[]): boolean => {
  if (!scores || scores.length === 0) return false;

  // Clean and sort the scores in descending order
  const cleanScores = scores.map((s) => s ?? 0).sort((a, b) => b - a);

  const topScore = cleanScores[0];
  const secondScore = cleanScores[1] || 0;

  return (
    topScore >= RELEVANCE_THRESHOLD &&
    (cleanScores.length === 1 || topScore - secondScore >= RELEVANCE_GAP_THRESHOLD)
  );
};

const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  currencyId: null,
  status: [MatterStatus.Draft, MatterStatus.ClientReview],
  updatedAt: { to: new Date(new Date().setDate(new Date().getDate() - 0)) },
  orderBy: [{ updatedAt: AscDesc.asc }],
  createdBy: null,
  currentPage: 1,
  pageSize: 20,
  feeEarner: null,
  someTimeEntries: null,
  linked: false,
};

export type QuickLinkModalContainerProps = {
  displayName?: string;
  pmsName?: string;
  forceOpen?: boolean;
  onClose?: () => void;
};

const formatRelevanceScore = (score: number | null | undefined): string => {
  if (score === null || score === undefined) return "0% match";
  return `${Math.round(score * 100)}% match`;
};

const isStaleDate = (date: string | Date, staleMatterCutoffDays: number): boolean => {
  const createdDate = new Date(date);
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - staleMatterCutoffDays);
  return createdDate < cutoffDate;
};

type ConfirmLinkSectionProps = {
  matter: {
    id: number;
    title: string;
    organisationalUnit: {
      id: number;
      name: string;
    } | null;
    client: {
      id: number;
      name: string;
    } | null;
  };
  externalMatterId: number | null;
  externalMatters: {
    id: number;
    title: string;
    organisationalUnit: {
      id: number;
      name: string;
    } | null;
    client?: {
      id: number;
      name: string;
      externalReference: string | null;
    } | null;
  }[];
  bestMatch?: {
    id: number;
    title: string;
    organisationalUnit: {
      id: number;
      name: string;
    } | null;
    client?: {
      id: number;
      name: string;
      externalReference: string | null;
    } | null;
  };
  pmsName: string;
  onConfirm: (
    matterId: number,
    externalMatterId: number,
    setLive: boolean,
    newClientId?: number
  ) => void;
  onCancel: () => void;
};

const ConfirmLinkSection: FC<ConfirmLinkSectionProps> = ({
  matter,
  externalMatterId,
  externalMatters,
  bestMatch,
  pmsName,
  onConfirm,
  onCancel,
}) => {
  const [showClientMismatchConfirmation, setShowClientMismatchConfirmation] =
    useState<boolean>(false);
  const [willSetLive, setWillSetLive] = useState<boolean>(false);

  const selectedExternalMatter = externalMatterId
    ? externalMatters.find((m) => m.id === externalMatterId)
    : bestMatch;

  // Check if practice areas match
  const vpdPracticeArea = matter.organisationalUnit?.id;
  const externalPracticeArea = selectedExternalMatter?.organisationalUnit?.id;
  const practiceAreasMatch =
    vpdPracticeArea && externalPracticeArea ? vpdPracticeArea === externalPracticeArea : false;

  // Check if clients match
  const vpdClient = matter.client?.id;
  const externalClient = selectedExternalMatter?.client?.id;
  const clientsMatch = vpdClient && externalClient ? vpdClient === externalClient : false;

  // Handle initial link button click
  const handleLinkClick = (setLive: boolean) => {
    if (!selectedExternalMatter) return;

    // If clients don't match, show the additional confirmation
    if (!clientsMatch && matter.client && selectedExternalMatter.client) {
      setShowClientMismatchConfirmation(true);
      setWillSetLive(setLive);
    } else {
      // If clients match, proceed directly
      onConfirm(matter.id, selectedExternalMatter.id, setLive);
    }
  };

  // Handle final confirmation after client mismatch warning
  const handleFinalConfirm = (newClientId?: number) => {
    if (!selectedExternalMatter) return;
    onConfirm(matter.id, selectedExternalMatter.id, willSetLive, newClientId);
  };

  // Display the client mismatch confirmation screen
  if (showClientMismatchConfirmation && selectedExternalMatter) {
    return (
      <Alert
        message="Client Mismatch Warning"
        description={
          <div className={confirmLinkDetailsClassName}>
            <Typography.Text strong>
              As part of the linking process we will update the client to match the client assigned
              to the matter in {pmsName}.
            </Typography.Text>

            <div className={confirmRowClassName} style={{ marginTop: "16px" }}>
              <div className={confirmSectionClassName}>
                <div className={confirmSectionTitleClassName}>
                  <strong>VPD Client:</strong> {matter.client?.name}
                </div>
              </div>
            </div>

            <div className={confirmArrowClassName}>
              <DownOutlined />
            </div>

            <div className={confirmRowClassName}>
              <div className={confirmSectionClassName}>
                <div className={confirmSectionTitleClassName}>
                  <strong>{pmsName} Client:</strong> {selectedExternalMatter.client?.name}
                  {selectedExternalMatter.client?.externalReference && (
                    <>- {selectedExternalMatter.client?.externalReference}</>
                  )}
                </div>
              </div>
            </div>

            <Typography.Text type="danger" strong style={{ marginTop: "16px" }}>
              Only proceed if you are sure that the {pmsName} client is the correct one.
            </Typography.Text>
          </div>
        }
        type="error"
        showIcon
        action={
          <div className={confirmActionButtonsClassName}>
            <Button
              type="primary"
              danger
              onClick={() => handleFinalConfirm(selectedExternalMatter.client?.id)}
            >
              Replace Client and Link
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </div>
        }
      />
    );
  }

  // Display the regular confirmation screen
  return (
    <Alert
      message="Confirm Link"
      description={
        <div className={confirmLinkDetailsClassName}>
          <Typography.Text>Are you sure you want to link the following matters?</Typography.Text>

          <div className={confirmRowClassName}>
            <div className={confirmSectionClassName}>
              <div className={confirmSectionTitleClassName}>
                <strong>VPD Matter:</strong> <Tooltip title={matter.title}>{matter.title}</Tooltip>
              </div>
              <div className={confirmSectionMetadataClassName}>
                {matter.organisationalUnit?.name && (
                  <div
                    className={`${confirmMetadataItemClassName} ${confirmPracticeAreaClassName} ${
                      practiceAreasMatch ? highConfidenceMatchCardClassName : ""
                    }`}
                  >
                    <strong>Practice Area:</strong> {matter.organisationalUnit.name}
                    {vpdPracticeArea &&
                      externalPracticeArea &&
                      (practiceAreasMatch ? (
                        <div className={matchIconClassName}>
                          <CheckCircleFilled />
                        </div>
                      ) : (
                        <div className={noMatchIconClassName}>
                          <CloseCircleFilled />
                        </div>
                      ))}
                  </div>
                )}
                {matter.client?.name && (
                  <div
                    className={`${confirmMetadataItemClassName} ${confirmClientInfoClassName} ${
                      clientsMatch ? highConfidenceMatchCardClassName : ""
                    }`}
                  >
                    <strong>Client:</strong> {matter.client.name}
                    {vpdClient &&
                      externalClient &&
                      (clientsMatch ? (
                        <div className={matchIconClassName}>
                          <CheckCircleFilled />
                        </div>
                      ) : (
                        <div className={noMatchIconClassName}>
                          <CloseCircleFilled />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={confirmArrowClassName}>
            <DownOutlined />
          </div>
          <div className={confirmRowClassName}>
            <div className={confirmSectionClassName}>
              <div className={confirmSectionTitleClassName}>
                <strong>{pmsName} Matter:</strong>{" "}
                <Tooltip title={selectedExternalMatter?.title}>
                  {selectedExternalMatter?.title}
                </Tooltip>
              </div>
              <div className={confirmSectionMetadataClassName}>
                {selectedExternalMatter?.organisationalUnit?.name && (
                  <div
                    className={`${confirmMetadataItemClassName} ${confirmPracticeAreaClassName} ${
                      practiceAreasMatch ? highConfidenceMatchCardClassName : ""
                    }`}
                  >
                    <strong>Practice Area:</strong> {selectedExternalMatter.organisationalUnit.name}
                    {vpdPracticeArea &&
                      externalPracticeArea &&
                      (practiceAreasMatch ? (
                        <div className={matchIconClassName}>
                          <CheckCircleFilled />
                        </div>
                      ) : (
                        <div className={noMatchIconClassName}>
                          <CloseCircleFilled />
                        </div>
                      ))}
                  </div>
                )}
                {selectedExternalMatter?.client?.name && (
                  <div
                    className={`${confirmMetadataItemClassName} ${confirmClientInfoClassName} ${
                      clientsMatch ? highConfidenceMatchCardClassName : ""
                    }`}
                  >
                    <strong>Client:</strong> {selectedExternalMatter.client.name}
                    {vpdClient &&
                      externalClient &&
                      (clientsMatch ? (
                        <div className={matchIconClassName}>
                          <CheckCircleFilled />
                        </div>
                      ) : (
                        <div className={noMatchIconClassName}>
                          <CloseCircleFilled />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      }
      type="warning"
      showIcon
      action={
        <div className={confirmActionButtonsClassName}>
          <Button type="primary" onClick={() => selectedExternalMatter && handleLinkClick(false)}>
            Link
          </Button>
          <Button
            type="primary"
            className={linkAndSetLiveButtonClassName}
            onClick={() => selectedExternalMatter && handleLinkClick(true)}
          >
            Link and set Live
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      }
    />
  );
};

export const QuickLinkModalContainer: FC<QuickLinkModalContainerProps> = ({
  displayName,
  pmsName = "PMS",
  forceOpen = false,
  onClose,
}): ReactElement | null => {
  const [filterData, setFilter] = useState<MatterFilter>({
    ...INITIAL_MATTER_FILTER,
  });
  const [confirming, setConfirming] = useState<number | null>(null);
  const [selectedExternalMatterId, setSelectedExternalMatterId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [expandedViewMode, setExpandedViewMode] = useState<{
    active: boolean;
    matterId: number | null;
    matterTitle: string | null;
  }>({
    active: false,
    matterId: null,
    matterTitle: null,
  });
  const [seenMatterIds, setSeenMatterIds] = useState<Set<number>>(new Set());
  const [staleMatterShowingLinking, setStaleMatterShowingLinking] = useState<number | null>(null);

  const { settings } = useSettings();
  const STALE_MATTER_CUTOFF_DAYS = settings?.staleMatterWarningThresholdInDays ?? 60;

  // Fetch user data
  const { loading: loadingUserData, data: userData } = useQuery(USER_DATA_QUERY, {
    onCompleted: (data) => {
      if (!data?.currentUserFeeEarnerData?.userId) return;
      setFilter((curr) => ({
        ...curr,
        createdBy: [data?.currentUserFeeEarnerData?.userId!, ...(filterData.createdBy ?? [])],
      }));
    },
  });

  const { pageSize, currentPage, ...filter } = filterData;

  // Fetch matters
  const { callFunc, data, loading } = useFilteredMattersLazyQuery({
    filter: {
      ...(filter?.title && { title: filter.title }),
      ...(filter?.client && { clientId: filter.client }),
      ...(filter?.status && { status: filter.status }),
      ...(filter?.updatedAt?.from && {
        updatedAtFrom: filter.updatedAt?.from,
      }),
      ...(filter?.updatedAt?.to && {
        updatedAtTo: filter.updatedAt?.to,
      }),
      ...(filter?.createdBy && { createdBy: filter.createdBy }),
      linked: filter.linked,
      template: false,
    },
    ...(filter?.orderBy && { orderBy: filter.orderBy }),
    offset: ((currentPage || 1) - 1) * (pageSize || 0),
    limit: pageSize,
  });

  // Fetch similar external matters
  const [
    findSimilarExternalMatters,
    { loading: loadingSimilarMatters, data: similarExternalMattersData },
  ] = useLazyQuery(FIND_SIMILAR_EXTERNAL_MATTERS_FOR_MATTERS);

  const similarExternalMatters = similarExternalMattersData?.findSimilarExternalMattersForMatters;

  // Initialize queries
  useEffect(() => {
    if (!loadingUserData && (filterData.createdBy?.length ?? 0) > 0) {
      callFunc();
    }
  }, [loadingUserData, filterData]);

  // Fetch similar matters only for new matters
  useEffect(() => {
    const matters = data?.filteredMatters?.matters;
    if (!matters || matters.length === 0) return;

    const currentMatterIds = new Set(matters.map((matter) => Number(matter.id)));
    const newMatterIds = Array.from(currentMatterIds).filter((id) => !seenMatterIds.has(id));

    if (newMatterIds.length > 0) {
      findSimilarExternalMatters({
        variables: {
          input: {
            matterIds: newMatterIds,
          },
        },
      });

      setSeenMatterIds(new Set([...seenMatterIds, ...newMatterIds]));
    }
  }, [data?.filteredMatters?.matters]);

  const [updateQuickUpdateShownAt] = useMutation(UPDATE_QUICK_UPDATE_SHOWN_AT);

  const [updateMatter, { loading: loadingUpdateMatter }] = useMutation(UPDATE_MATTER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            skip: 0,
            take: 10,
            matterStatus: MatterStatus.Live,
          },
        },
      },
      {
        query: NOTIFICATIONS_QUERY,
        variables: { type: "Alert" },
      },
    ],
    update: (cache, { data }) => {
      const updatedMatterId = data?.updateMatter?.id;
      if (!updatedMatterId) return;
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "findAccruedFeesForUser",
      });
      // If the matter is closed or lost, or has been linked, remove it from the list
      if (
        data?.updateMatter?.status === MatterStatus.Closed ||
        data?.updateMatter?.status === MatterStatus.Lost ||
        data?.updateMatter?.externalMatter?.id
      ) {
        cache.modify({
          fields: {
            filteredMatters(existing = {}, { readField }) {
              if (!existing || !existing.matters) return;
              const newMatters = existing.matters.filter((mRef: any) => {
                const matterId = readField("id", mRef);
                return matterId !== updatedMatterId;
              });

              const result = {
                ...existing,
                matters: newMatters,
                count: existing.count - 1,
              };

              return result;
            },
          },
        });
      }
    },
  });

  const [quickLinkMatter, { loading: loadingQuickLinkMatter }] = useMutation(QUICK_LINK_MATTER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            skip: 0,
            take: 10,
            matterStatus: MatterStatus.Live,
          },
        },
      },
      {
        query: NOTIFICATIONS_QUERY,
        variables: { type: "Alert" },
      },
    ],
    update: (cache, { data }) => {
      const updatedMatterId = data?.quickLinkMatter?.id;
      if (!updatedMatterId) return;
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "findAccruedFeesForUser",
      });
      // If the matter is closed or lost, or has been linked, remove it from the list
      if (
        data?.quickLinkMatter?.status === MatterStatus.Closed ||
        data?.quickLinkMatter?.status === MatterStatus.Lost ||
        data?.quickLinkMatter?.externalMatter?.id
      ) {
        cache.modify({
          fields: {
            filteredMatters(existing = {}, { readField }) {
              if (!existing || !existing.matters) return;
              const newMatters = existing.matters.filter((mRef: any) => {
                const matterId = readField("id", mRef);
                return matterId !== updatedMatterId;
              });

              const result = {
                ...existing,
                matters: newMatters,
                count: existing.count - 1,
              };

              return result;
            },
          },
        });
      }
    },
  });

  // Handler for linking an external matter
  const handleLinkMatter = async (
    matterId: number,
    externalMatterId: number,
    setLive: boolean,
    newClientId?: number,
    relevanceScore?: number,
    gap?: number,
    rankPosition?: number,
    highConfidenceMatch?: boolean,
    forceOpenModal?: boolean
  ) => {
    await quickLinkMatter({
      variables: {
        input: {
          matterId,
          externalMatterId,
          ...(newClientId && { newClientId }),
          ...(setLive && { setLive }),
          ...(relevanceScore && { relevanceScore }),
          ...(gap && { gap }),
          ...(rankPosition && { rankPosition }),
          ...(highConfidenceMatch !== undefined && { highConfidenceMatch }),
          ...(forceOpenModal !== undefined && { forceOpenModal }),
        },
      },
    });
    Userpilot.track("quick_matter_linked");
    setConfirming(null);
  };

  const filteredMatters = data?.filteredMatters.matters || [];
  const count = data?.filteredMatters.count || 0;

  const [show, setShow] = useState(count > 0);
  const modalOpenTracked = useRef(false);

  // Track when modal opens
  useEffect(() => {
    if (show && !modalOpenTracked.current) {
      Userpilot.track("quick_matter_link_modal_loaded");
      modalOpenTracked.current = true;
    } else if (!show) {
      modalOpenTracked.current = false;
    }
  }, [show]);

  const mattersWithExternalMatters = filteredMatters.map((matter) => {
    const externalMatters =
      similarExternalMatters?.results?.find((item) => Number(item.matterId) === Number(matter.id))
        ?.externalMatters || [];
    const sortedExternalMatters = [...externalMatters].sort(
      (a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0)
    );

    return {
      ...matter,
      externalMatters: sortedExternalMatters,
      bestMatch: sortedExternalMatters.length > 0 ? sortedExternalMatters[0].relevanceScore : null,
    };
  });

  // Filter matters based on showAllMatches state when not forced open
  const displayedMatters = mattersWithExternalMatters;

  // Identify high confidence matches and sort matters accordingly
  const sortedMatters = displayedMatters.sort((a, b) => {
    // Calculate high confidence for each matter
    const aMatters = a.externalMatters || [];
    const bMatters = b.externalMatters || [];

    const aScores = aMatters.map((m) => m.relevanceScore);
    const bScores = bMatters.map((m) => m.relevanceScore);

    const aIsHighConfidence = isHighConfidenceMatch(aScores);
    const bIsHighConfidence = isHighConfidenceMatch(bScores);

    // If one is high confidence and the other isn't, prioritize high confidence
    if (aIsHighConfidence && !bIsHighConfidence) return -1;
    if (!aIsHighConfidence && bIsHighConfidence) return 1;

    // If both or neither are high confidence, sort by best match score
    return (b.bestMatch ?? 0) - (a.bestMatch ?? 0);
  });

  // Effect to handle automatic showing of the modal based on data
  useEffect(() => {
    const lastShownAt = new Date(userData?.currentUserFeeEarnerData?.quickUpdateShownAt);
    const shownToday = lastShownAt.toDateString() === new Date().toDateString();

    // Check if the modal hasn't been shown in the past week
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const shownThisWeek = lastShownAt > oneWeekAgo;

    // Only show automatically if not being forced open and all data has been loaded
    const dataIsReady =
      !loading &&
      !loadingUserData &&
      !loadingSimilarMatters &&
      (data?.filteredMatters?.matters?.length === 0 || similarExternalMattersData !== undefined);

    // Check if there's at least one high confidence match
    const hasHighConfidenceMatches =
      similarExternalMattersData?.findSimilarExternalMattersForMatters?.results.some((result) => {
        const scores = (result.externalMatters || []).map((ext) => ext.relevanceScore);
        return isHighConfidenceMatch(scores);
      }) ?? false;

    // Check if there are stale matters
    const hasStaleMatters =
      data?.filteredMatters?.matters?.some((matter) =>
        isStaleDate(matter.createdAt, STALE_MATTER_CUTOFF_DAYS)
      ) ?? false;

    // Show if:
    // 1. Not shown today AND has high confidence matches or stale matters OR
    // 2. Not shown in the past week (regardless of matches)
    if (
      !forceOpen &&
      dataIsReady &&
      ((!shownThisWeek && count > 0) ||
        (!shownToday && (hasHighConfidenceMatches || hasStaleMatters)))
    ) {
      setShow(count > 0);
      if (userData?.currentUserFeeEarnerData?.userId) {
        updateQuickUpdateShownAt();
      }
    }
  }, [
    count,
    loading,
    loadingUserData,
    loadingSimilarMatters,
    data?.filteredMatters?.matters,
    similarExternalMattersData,
  ]);

  // Separate effect to handle forceOpen changes
  useEffect(() => {
    setShow(forceOpen);
  }, [forceOpen]);

  const isLoading =
    loading ||
    loadingUpdateMatter ||
    loadingQuickLinkMatter ||
    loadingUserData ||
    loadingSimilarMatters;

  // Modified toggle function to handle the expanded view mode
  const toggleMatchesList = (matterId: number, matterTitle?: string) => {
    // If we're already in expanded view mode for this matter, exit expanded view
    if (expandedViewMode.active && expandedViewMode.matterId === matterId) {
      setExpandedViewMode({
        active: false,
        matterId: null,
        matterTitle: null,
      });
    } else {
      // Enter expanded view mode for this matter
      setExpandedViewMode({
        active: true,
        matterId,
        matterTitle: matterTitle || null,
      });
    }

    // Clear confirming state when expanding matches
    if (confirming === matterId) {
      setConfirming(null);
      setSelectedExternalMatterId(null);
    }
  };

  const confirmLinkMatter = (matterId: number, externalMatterId: number) => {
    Userpilot.track("quick_matter_link_button_clicked");
    // Set confirming state for the UI
    setConfirming(matterId);
    setSelectedExternalMatterId(externalMatterId);
  };

  // Helper function to highlight matched text
  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return <span>{text}</span>;

    const parts = text.split(new RegExp(`(${searchTerm})`, "i"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <span key={i} className={textMatchHighlightClassName}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };

  const handleClose = () => {
    setShow(false);
    if (onClose) {
      onClose();
    }
  };

  // Render empty state
  if (count === 0 && !isLoading && !forceOpen) {
    return null;
  }

  return (
    <Modal
      title={`Welcome back${displayName ? `, ${displayName}` : ""}!`}
      open={show}
      width="60%"
      onCancel={handleClose}
      maskClosable={false}
      bodyStyle={{ padding: "16px" }}
      style={{ top: 20 }}
      footer={[
        <Button type="primary" key="ok" onClick={handleClose}>
          Done
        </Button>,
      ]}
    >
      <div className={quickLinkContainerClassName}>
        <Typography.Title level={4} style={{ marginBottom: 12 }}>
          Link Your Matters to {pmsName} Matters
        </Typography.Title>

        {!expandedViewMode.active ? (
          <>
            <List
              loading={isLoading}
              dataSource={sortedMatters}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <Typography.Text>Good job, no unlinked matters to show here!</Typography.Text>
                    }
                  />
                ),
              }}
              renderItem={(matter) => {
                const matterExternalMatters = matter.externalMatters;

                const bestMatch = matterExternalMatters[0];

                const isHighRelevance = isHighConfidenceMatch(
                  matterExternalMatters.map((m) => m.relevanceScore)
                );

                return (
                  <Card
                    className={`${baseCardClassName} ${quickLinkCardClassName} ${
                      isStaleDate(matter.createdAt, STALE_MATTER_CUTOFF_DAYS)
                        ? staleMatterClassName
                        : isHighRelevance
                        ? highRelevanceMatterClassName
                        : ""
                    }`}
                    key={matter.id}
                    size="small"
                    bodyStyle={{ padding: "12px" }}
                  >
                    {confirming === matter.id ? (
                      // Full-width confirmation UI
                      <Row
                        gutter={[16, 16]}
                        align="middle"
                        className={`${comparisonRowClassName} ${confirmLinkRowClassName}`}
                      >
                        <Col span={24}>
                          <ConfirmLinkSection
                            matter={matter}
                            externalMatterId={selectedExternalMatterId}
                            externalMatters={matterExternalMatters}
                            bestMatch={bestMatch}
                            pmsName={pmsName}
                            onConfirm={(matterId, externalMatterId, setLive, newClientId) => {
                              const relevanceScore =
                                matterExternalMatters.find((m) => m.id === externalMatterId)
                                  ?.relevanceScore ?? undefined;
                              const selectedIdx = matterExternalMatters.findIndex(
                                (m) => m.id === externalMatterId
                              );
                              const rankPosition = selectedIdx >= 0 ? selectedIdx + 1 : undefined;
                              const isHighConfidence =
                                selectedIdx === 0 &&
                                isHighConfidenceMatch(
                                  matterExternalMatters.map((m) => m.relevanceScore)
                                );
                              // Calculate gap
                              const scores = matterExternalMatters
                                .map((m) => m.relevanceScore ?? 0)
                                .sort((a, b) => b - a);
                              const topScore = scores[0] ?? 0;
                              const secondScore = scores[1] ?? 0;
                              const gap = scores.length === 1 ? topScore : topScore - secondScore;

                              handleLinkMatter(
                                matterId,
                                externalMatterId,
                                setLive,
                                newClientId,
                                relevanceScore,
                                gap,
                                rankPosition,
                                isHighConfidence,
                                forceOpen
                              );
                              setConfirming(null);
                              setSelectedExternalMatterId(null);
                            }}
                            onCancel={() => {
                              setConfirming(null);
                              setSelectedExternalMatterId(null);
                            }}
                          />
                        </Col>
                      </Row>
                    ) : (
                      <Row gutter={[16, 16]} align="middle" className={comparisonRowClassName}>
                        {/* VPD Matter Side */}
                        <Col span={9} className={vpdMatterColClassName}>
                          <Card
                            size="small"
                            className={`${baseCardClassName} ${innerCardClassName} ${vpdMatterCardClassName}`}
                            title={
                              <div className={matterTitleClassName}>
                                <Tooltip title={matter.title}>
                                  <Typography.Text strong ellipsis>
                                    {matter.title}
                                  </Typography.Text>
                                </Tooltip>
                              </div>
                            }
                            bordered={false}
                          >
                            <div className={cardContentClassName}>
                              <MatterStatusSelectorDropdown
                                status={matter.status}
                                id={matter.id}
                                isParent={false}
                                reference={""}
                                onUpdate={async (matter) => {
                                  await updateMatter({
                                    variables: {
                                      id: Number(matter.id),
                                      data: {
                                        status: { set: matter.status },
                                      },
                                    },
                                  });
                                }}
                                externalMatterId={null}
                                pmsName={pmsName}
                                allowLive={false}
                                tooltipMessage="Link a matter first to set it to live"
                              />
                              <div className={matterMetadataClassName}>
                                <Typography.Text type="secondary">
                                  Client: {matter.client?.name}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                  Practice Area: {matter.organisationalUnit?.name || "Not assigned"}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                  Created at:{" "}
                                  <span
                                    style={{
                                      color: isStaleDate(matter.createdAt, STALE_MATTER_CUTOFF_DAYS)
                                        ? "red"
                                        : "inherit",
                                    }}
                                  >
                                    {new Date(matter.createdAt).toLocaleDateString()}
                                  </span>
                                </Typography.Text>
                              </div>
                            </div>
                          </Card>
                        </Col>

                        {/* Arrow Column */}
                        <Col span={1} className={arrowColClassName}>
                          <div className={arrowContainerClassName}>
                            {!isStaleDate(matter.createdAt, STALE_MATTER_CUTOFF_DAYS) &&
                              matterExternalMatters.length > 0 && (
                                <ArrowRightOutlined className={arrowIconClassName} />
                              )}
                          </div>
                        </Col>

                        {/* External Matter Side or Stale Matter Alert */}
                        {isStaleDate(matter.createdAt, STALE_MATTER_CUTOFF_DAYS) &&
                        staleMatterShowingLinking !== matter.id ? (
                          <>
                            <Col span={11} className={externalMatterColClassName}>
                              <Card
                                size="small"
                                style={{
                                  borderLeft: "4px solid red",
                                  backgroundColor: "#fff1f0",
                                }}
                                className={`${baseCardClassName} ${innerCardClassName} ${staleMatterAlertClassName}`}
                                title={
                                  <div className={externalTitleClassName}>
                                    <WarningOutlined
                                      style={{ fontSize: 20, color: "red", marginRight: 8 }}
                                    />
                                    <Typography.Text strong>Stale Matter Detected</Typography.Text>
                                  </div>
                                }
                                bordered={false}
                              >
                                <div className={cardContentClassName}>
                                  <Typography.Text type="secondary">
                                    This matter was created over {STALE_MATTER_CUTOFF_DAYS} days
                                    ago. Consider marking it as Closed or Lost.
                                  </Typography.Text>

                                  <div className={staleMatterCardButtonsClassName}>
                                    <Button
                                      type="default"
                                      icon={<EditOutlined />}
                                      onClick={() => {
                                        Userpilot.track("quick_matter_lost_closed");
                                        updateMatter({
                                          variables: {
                                            id: Number(matter.id),
                                            data: { status: { set: MatterStatus.Closed } },
                                          },
                                        });
                                      }}
                                    >
                                      Closed
                                    </Button>
                                    <Button
                                      type="default"
                                      icon={<EditOutlined />}
                                      onClick={() => {
                                        Userpilot.track("quick_matter_lost_closed");
                                        updateMatter({
                                          variables: {
                                            id: Number(matter.id),
                                            data: { status: { set: MatterStatus.Lost } },
                                          },
                                        });
                                      }}
                                    >
                                      Lost
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            </Col>
                            <Col span={3} className={actionButtonsContainerClassName}>
                              <Button
                                type="default"
                                className={linkMatterButtonClassName}
                                icon={<LinkOutlined />}
                                onClick={() => setStaleMatterShowingLinking(matter.id)}
                              >
                                Link Anyway
                              </Button>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col span={11} className={externalMatterColClassName}>
                              {matterExternalMatters.length === 0 ? (
                                <Empty
                                  description={`No ${pmsName} matters found`}
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  className={emptyStateClassName}
                                />
                              ) : (
                                <Card
                                  size="small"
                                  className={`${baseCardClassName} ${innerCardClassName} ${externalMatterCardClassName} ${
                                    isHighRelevance ? highRelevanceCardClassName : ""
                                  }`}
                                  title={
                                    <div className={externalTitleClassName}>
                                      <Tooltip title={bestMatch?.title}>
                                        <Typography.Text strong ellipsis>
                                          {bestMatch?.title}
                                        </Typography.Text>
                                      </Tooltip>
                                    </div>
                                  }
                                  bordered={false}
                                >
                                  <div className={cardContentClassName}>
                                    {isHighRelevance ? (
                                      <Tag color="success" className={confidenceTagClassName}>
                                        High Confidence Match
                                      </Tag>
                                    ) : (
                                      <Tag color="blue" className={scoreTagClassName}>
                                        {formatRelevanceScore(bestMatch?.relevanceScore)}
                                      </Tag>
                                    )}
                                    <div className={matterMetadataClassName}>
                                      <Typography.Text
                                        type="secondary"
                                        className={externalDescriptionClassName}
                                      >
                                        Best match based on relevance score
                                      </Typography.Text>
                                      <Typography.Text type="secondary">
                                        Practice Area:{" "}
                                        {bestMatch?.organisationalUnit?.name || "Not assigned"}
                                      </Typography.Text>
                                      <Typography.Text type="secondary">
                                        Created at:{" "}
                                        {new Date(
                                          bestMatch?.externalCreatedAt
                                        ).toLocaleDateString()}
                                      </Typography.Text>
                                    </div>
                                  </div>
                                </Card>
                              )}
                            </Col>

                            {/* Action Buttons Column */}
                            <Col span={3} className={actionButtonsContainerClassName}>
                              {matterExternalMatters.length > 0 ? (
                                <>
                                  <Button
                                    type="primary"
                                    className={linkMatterButtonClassName}
                                    icon={<LinkOutlined />}
                                    onClick={() => confirmLinkMatter(matter.id, bestMatch?.id)}
                                  >
                                    Link Matter
                                  </Button>

                                  {matterExternalMatters.length > 1 && (
                                    <Button
                                      type="default"
                                      className={showMoreButtonClassName}
                                      icon={<DownOutlined />}
                                      onClick={() => toggleMatchesList(matter.id, matter.title)}
                                    >
                                      Show More
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <div className={noActionsClassName}>
                                  <Typography.Text type="secondary">
                                    No actions available
                                  </Typography.Text>
                                </div>
                              )}
                            </Col>
                          </>
                        )}
                      </Row>
                    )}
                  </Card>
                );
              }}
            />
          </>
        ) : (
          // Full Modal Expanded Matches View
          <div className={`${expandedMatchesContainerClassName} ${fullModalExpandedViewClassName}`}>
            {/* If confirming, show confirmation UI */}
            {confirming && expandedViewMode.matterId === confirming ? (
              <div className={expandedMatchesScrollClassName}>
                <ConfirmLinkSection
                  matter={sortedMatters.find((m) => m.id === confirming)!}
                  externalMatterId={selectedExternalMatterId}
                  externalMatters={
                    sortedMatters.find((m) => m.id === confirming)?.externalMatters || []
                  }
                  pmsName={pmsName}
                  onConfirm={(matterId, externalMatterId, setLive, newClientId) => {
                    const matterObj = sortedMatters.find((m) => m.id === matterId);
                    const matterExternalMatters = matterObj?.externalMatters ?? [];
                    const relevanceScore =
                      matterExternalMatters.find((m) => m.id === externalMatterId)
                        ?.relevanceScore ?? undefined;
                    const selectedIdx = matterExternalMatters.findIndex(
                      (m) => m.id === externalMatterId
                    );
                    const rankPosition = selectedIdx >= 0 ? selectedIdx + 1 : undefined;
                    const isHighConfidence =
                      selectedIdx === 0 &&
                      isHighConfidenceMatch(matterExternalMatters.map((m) => m.relevanceScore));
                    // Calculate gap
                    const scores = matterExternalMatters
                      .map((m) => m.relevanceScore ?? 0)
                      .sort((a, b) => b - a);
                    const topScore = scores[0] ?? 0;
                    const secondScore = scores[1] ?? 0;
                    const gap = scores.length === 1 ? topScore : topScore - secondScore;

                    handleLinkMatter(
                      matterId,
                      externalMatterId,
                      setLive,
                      newClientId,
                      relevanceScore,
                      gap,
                      rankPosition,
                      isHighConfidence,
                      forceOpen
                    );
                    setConfirming(null);
                    setSelectedExternalMatterId(null);
                  }}
                  onCancel={() => {
                    setConfirming(null);
                    setSelectedExternalMatterId(null);
                  }}
                />
              </div>
            ) : (
              <>
                <div className={expandedMatchesHeaderClassName}>
                  <div>
                    <Typography.Text strong className={expandedMatchesTitleClassName}>
                      All Potential Matches for "{expandedViewMode.matterTitle}"
                    </Typography.Text>
                    <div className={expandedMatchesSubtitleClassName}>
                      <Typography.Text type="secondary">
                        {
                          sortedMatters.find((m) => m.id === expandedViewMode.matterId)
                            ?.externalMatters.length
                        }{" "}
                        potential {pmsName} matches found
                      </Typography.Text>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                      type={isSearchActive ? "primary" : "default"}
                      icon={<SearchOutlined />}
                      onClick={() => {
                        setIsSearchActive(!isSearchActive);
                        setSearchTerm("");
                      }}
                      className={`${searchButtonClassName} ${
                        isSearchActive ? searchButtonActiveClassName : ""
                      }`}
                    >
                      Search
                    </Button>
                    <Button
                      type="text"
                      icon={<UpOutlined />}
                      onClick={() => {
                        setIsSearchActive(false);
                        expandedViewMode.matterId && toggleMatchesList(expandedViewMode.matterId);
                      }}
                    >
                      Back to Matters
                    </Button>
                  </div>
                </div>

                {isSearchActive && (
                  <div className={searchBarContainerClassName}>
                    <div className={searchInputWrapperClassName}>
                      <SearchOutlined className={searchIconClassName} />
                      <input
                        type="text"
                        className={searchInputClassName}
                        placeholder="Filter matches by title..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                      {searchTerm && (
                        <Button
                          type="text"
                          size="small"
                          className={searchClearButtonClassName}
                          onClick={() => setSearchTerm("")}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className={expandedMatchesScrollClassName}>
                  <List
                    dataSource={sortedMatters.filter(
                      (matter) => matter.id === expandedViewMode.matterId
                    )}
                    renderItem={(matter) => {
                      const matterExternalMatters = matter.externalMatters || [];
                      const filteredExternalMatters = matterExternalMatters.filter(
                        (m) =>
                          !searchTerm || m.title?.toLowerCase().includes(searchTerm.toLowerCase())
                      );

                      return (
                        <React.Fragment>
                          {filteredExternalMatters.map((externalMatter, index) => {
                            const scores = matterExternalMatters.map((m) => m.relevanceScore);
                            const currentScore = externalMatter.relevanceScore ?? 0;

                            // For the first item, check for high confidence
                            const externalItemRelevance =
                              index === 0 && isHighConfidenceMatch(scores);

                            return (
                              <List.Item
                                key={externalMatter.id}
                                className={`${matchItemClassName} ${
                                  externalItemRelevance ? highRelevanceItemClassName : ""
                                }`}
                                actions={[
                                  <Button
                                    key="link"
                                    type="primary"
                                    size="small"
                                    icon={<LinkOutlined />}
                                    onClick={() => {
                                      confirmLinkMatter(matter.id, externalMatter.id);
                                    }}
                                  >
                                    Link
                                  </Button>,
                                ]}
                              >
                                <List.Item.Meta
                                  title={
                                    <div className={externalTitleClassName}>
                                      <Tooltip title={externalMatter.title}>
                                        {searchTerm ? (
                                          highlightMatch(externalMatter.title || "", searchTerm)
                                        ) : (
                                          <span>{externalMatter.title}</span>
                                        )}
                                      </Tooltip>
                                      {externalItemRelevance ? (
                                        <Tag color="success" style={{ marginLeft: 8 }}>
                                          High match
                                        </Tag>
                                      ) : (
                                        <Tag color="blue" style={{ marginLeft: 8 }}>
                                          {formatRelevanceScore(externalMatter.relevanceScore)}
                                        </Tag>
                                      )}
                                    </div>
                                  }
                                  description={
                                    <div className={matterMetadataClassName}>
                                      {externalMatter.client?.name && (
                                        <Typography.Text type="secondary">
                                          Client: {externalMatter.client?.name}{" "}
                                          {externalMatter.externalCreatedAt && (
                                            <>
                                              <span>•</span> Created on:{" "}
                                              {new Date(
                                                externalMatter.externalCreatedAt
                                              ).toLocaleDateString()}
                                            </>
                                          )}
                                        </Typography.Text>
                                      )}
                                      {externalMatter.organisationalUnit?.name && (
                                        <Typography.Text type="secondary">
                                          Practice Area: {externalMatter.organisationalUnit?.name}
                                        </Typography.Text>
                                      )}
                                    </div>
                                  }
                                />
                              </List.Item>
                            );
                          })}
                        </React.Fragment>
                      );
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
